import { useState } from "react";
import { useTranslation } from "react-i18next";
import LogoHeader from "../../components/header/logoHeader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { loginUser } from "../../redux/slices/loginSlice";
import Button from "../../components/common/imageButton";

/**
 * Auth Component
 * Handles user authentication by allowing login via user ID and password.
 */
function Auth() {
  const { t } = useTranslation(); // Translation hook for internationalization
  const dispatch = useDispatch(); // Hook to dispatch Redux actions
  const navigate = useNavigate(); // Navigation hook to programmatically navigate routes

  // Extract loading state from Redux store
  const { isLoading } = useSelector((state) => state.auth);

  // Local state to manage user ID, password, error messages, and password visibility
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);


  const validateInputs = (name, value) => {
    if (name === "userId" && !value) {
      setLocalError(t("auth.error_empty_userid"));
    } else if (name === "password" && !value) {
      setLocalError(t("auth.error_empty_password"));
    } else {
      setLocalError("");
    }
  };

  /**
   * Handle user ID input changes.
   *
   * @param {Object} e - Event object from the input field
   */
  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
    validateInputs("userId", e.target.value);
  };

  /**
   * Handle password input changes.
   *
   * @param {Object} e - Event object from the input field
   */
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    validateInputs("password", e.target.value);
  };

  /**
   * Toggle password visibility.
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Handle login functionality.
   * Validates user ID and password, and dispatches the login action.
   */
  const handleLogin = async () => {
    // Validate user ID and password
    if (!userId) {
      setLocalError(t("auth.error_empty_userid"));
      return;
    }

    if (!password) {
      setLocalError(t("auth.error_empty_password"));
      return;
    }

    try {
      // Prepare the payload based on verification status
      const payload = {
        user_id: userId,
        password: password,
      };

      // Dispatch login action and wait for the response
      const response = await dispatch(loginUser(payload)).unwrap();

      // Extract and store is_verified from the response in local storage
      const { is_verified, token,is_payment_verified,is_password_set } = response;
      localStorage.setItem("is_verified", is_verified);
      localStorage.setItem("is_password_set", is_password_set);
      localStorage.setItem("user_id", userId);
      localStorage.setItem("is_payment_verified", is_payment_verified);

      // Show success message and navigate to the appropriate page
      toast.success(t("auth.login_success"));

      // Navigate to appropriate page based on verification status
      if (is_password_set=== false) {
        navigate("/set-password", { replace: true });
      } else {
        localStorage.setItem("token", token);
        navigate("/");
      }
    } catch (error) {
      // Handle errors and display appropriate messages
      toast.error(error);
    }
  };

  return (
    <>
      {/* Logo header for branding */}
      <LogoHeader />
      <div className="flex justify-center items-center mt-5">
        <div className="border-4 border-[#E2A32C] rounded-xl p-5 shadow-lg w-full max-w-md mx-4">
          <h2 className="text-2xl font-semibold mb-8 text-center text-white">
            {t("auth.title")}
          </h2>
          <div className="space-y-4">
            <input
              type="text"
              placeholder={t("auth.user_id_placeholder")}
              value={userId}
              onChange={handleUserIdChange}
              className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("auth.password_placeholder")}
                value={password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <AiFillEye size={20} />
                )}
              </button>
            </div>
          </div>
          {localError ? (
            <div className="mt-2 ">
              <p className="text-red-500 text-sm text-center">{localError}</p>
            </div>
          ) : (
            <div className="mt-9">
              <p></p>
            </div>
          )}
          <div className="mt-2">
            <Button
              text={isLoading ? t("loading") : t("auth.button_title")}
              onClick={handleLogin}
              title={t("auth.title")}
              buttonTitle={t("auth.button_title")}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center mt-4">
        <img
          src="images/box.png"
          alt="Jackpot Box"
          className="w-36 h-40 mt-4 "
        />
      </div>
    </>
  );
}

export default Auth;
